import { useMemo } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  CONTENT_SEARCH_DOCUMENTS_CAP,
  useWorkspaceContentSearch,
} from "@/hooks/useWorkspaceContentSearch";
import type { TextMatch } from "@/pages/workspace/utils/workspaceItemUtils";

/** The files tab filters names as the reader types; the index is asked once typing pauses. */
const TEXT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Documents of one workspace whose text matches the files tab search (LAR-235), from the
 * same index the Workspaces page searches, so a search carried in by "Åbn workspace"
 * finds the same documents inside the workspace. Name matches do not wait for it.
 *
 * While the term is still being typed, or its answer is on its way, the hits of the
 * previous term are withheld: they would show documents the new term does not match.
 */
export const useWorkspaceTextMatches = (
  workspaceId: string | undefined,
  searchString: string
): { textMatches: ReadonlyMap<string, TextMatch> } => {
  const term = searchString.trim();
  const debouncedTerm = useDebounce(term, TEXT_SEARCH_DEBOUNCE_MS);

  const { workspaces, isSearchable, isPlaceholderData } =
    useWorkspaceContentSearch({
      searchTerm: debouncedTerm,
      workspaceIds: workspaceId ? [workspaceId] : undefined,
      workspacePageSize: 1,
      documentsPerWorkspace: CONTENT_SEARCH_DOCUMENTS_CAP,
      enabled: Boolean(workspaceId),
    });

  const textMatches = useMemo(() => {
    const matches = new Map<string, TextMatch>();
    if (
      !workspaceId ||
      !isSearchable ||
      isPlaceholderData ||
      debouncedTerm !== term
    ) {
      return matches;
    }
    for (const document of workspaces.flatMap((w) => w.documents)) {
      matches.set(document.id, {
        contentMatchCount: document.contentMatchCount,
        searchIndexStale: document.searchIndexStale,
      });
    }
    return matches;
  }, [
    workspaceId,
    isSearchable,
    isPlaceholderData,
    debouncedTerm,
    term,
    workspaces,
  ]);

  return { textMatches };
};
